import { Icon, IconButton } from "@smartsuite/react-ui/lib";
import { observer } from "mobx-react-lite";
import * as S from "../style";
import type { ValueConfig } from "@smartsuite/automation-engine-api";
import { useTranslation } from "react-i18next";
import { useReferenceHook } from "../utils/reference.hook";
import { Fragment } from "react";
import type { ReactElement } from "react";
import { translateIfPresent } from "../../../../../../../../../locales/i18n";
import { Colors, TextColorNames } from "@smartsuite/types";
import { Space } from "../style";
import type { ActionCandidate } from "../../../../../../../../../types/candidate";

export const StringValue = observer<{
  label: string | number;
  value?: ValueConfig;
  onlyStructure?: boolean;
  onRemove?: (index: number) => void;
  onEdit?: (index: number) => void;
  index?: number;
  fromArray?: boolean;
  action?: ActionCandidate;
}>(
  ({
    label,
    value,
    onlyStructure,
    onRemove,
    onEdit,
    index,
    fromArray,
    action,
  }) => {
    const { t } = useTranslation();
    const { getOutputRef } = useReferenceHook(value, action);
    const getText = (value?: ValueConfig): ReactElement[] => {
      if (!value) {
        return [<Fragment key="empty" />];
      }
      if (onlyStructure) {
        return [
          <S.Text key="structure" marginRight={"4px"}>
            {fromArray
              ? `${t("caption.List")}: ${t("caption.Text")}`
              : t("caption.Text")}
          </S.Text>,
        ];
      }
      return value.values.map((v, vIndex) => {
        if (v.value.oneofKind === "string") {
          return (
            <S.Text key={vIndex} marginRight={"4px"}>
              {v.value.string}
            </S.Text>
          );
        }
        if (v.value.oneofKind === "number") {
          return (
            <S.Text key={vIndex} marginRight={"4px"}>
              {v.value.number.toString()}
            </S.Text>
          );
        }
        if (v.value.oneofKind === "bool") {
          return (
            <S.Text key={vIndex} marginRight={"4px"}>
              {v.value.bool ? "true" : "false"}
            </S.Text>
          );
        }
        if (v.value.oneofKind === "reference") {
          const ref = getOutputRef(v.value.reference);
          const isNoError =
            ref !== undefined &&
            // @ts-expect-error - isError expected to be a boolean
            !ref.isError &&
            !JSON.parse(ref.value ?? "{}").isError;
          return (
            <S.PillWrapper
              key={vIndex}
              text={translateIfPresent(
                `caption.${ref?.title ?? v.value.reference.outputId}`,
                ref?.title ?? v.value.reference.outputId,
              )}
              styles={{
                backgroundDefault: isNoError ? Colors.dwBlue : Colors.wPink,
              }}
            />
          );
        }
        return <Fragment key={vIndex} />;
      });
    };
    let labelText = `${label}:`;

    if (onlyStructure) {
      labelText = fromArray
        ? `${t("caption.List")}: ${t("caption.Text")}`
        : t("caption.Text");
      labelText = index !== undefined && !fromArray ? `${label}:` : "";
    }

    return (
      <S.Root>
        <span>
          <S.IconWrapper>
            <Icon name="text" size="S" color={TextColorNames.tertiary} />
          </S.IconWrapper>
          <S.Text>{labelText}</S.Text>
          {getText(value)}
        </span>
        <S.StyledRemoveButton>
          <IconButton
            name="edit-thin"
            size="S"
            ariaLabel="Edit Button"
            color="currentColor"
            hoverColor="currentColor"
            onClick={() => index !== undefined && onEdit && onEdit(index)}
          />
          <Space />
          <IconButton
            name="trash-thin"
            size="S"
            ariaLabel="Remove Button"
            color="currentColor"
            hoverColor="currentColor"
            onClick={() => index !== undefined && onRemove && onRemove(index)}
          />
        </S.StyledRemoveButton>
      </S.Root>
    );
  },
);
